import { getUserId } from "../lib/effectiveAuthUser";
import { useState, useRef, useCallback, useEffect } from "react";
import firebase, { db } from "../Firebase";
import { useStoryBibleStore } from "../stores/storyBibleStore";
import { sendInvocationEvent } from "../Utils";
import { BENCHMARK_MODEL_LABEL } from "../components/BenchmarkTakeover/proseMode";

export function useComparisonRestore({
  documentId,
  existingCards,
  existingCardsLoaded,
  editor,
  editorEndRef,
}) {
  const [comparisonMode, setComparisonMode] = useState(false);
  const [comparisonVisible, setComparisonVisible] = useState(false);
  const comparisonEditorsRef = useRef(null);
  const comparisonInvocationIdRef = useRef(null);
  const streamBSceneIndexRef = useRef(null);

  const setScenes = useStoryBibleStore((state) => state.setScenes);
  const sceneId = useStoryBibleStore((state) => state.sceneId);

  const clearPendingComparison = useCallback(() => {
    const userId = getUserId();
    if (userId && documentId) {
      db.collection("userData").doc(userId).collection("documentMetas").doc(documentId)
        .update({
          pendingComparisonInvocationId: firebase.firestore.FieldValue.delete(),
          pendingComparisonStreamBSceneIndex: firebase.firestore.FieldValue.delete(),
        })
        .catch(() => {});
    }
  }, [documentId]);

  const handlePick = useCallback(
    (chosenEditor, pickedStream) => {
      const json = chosenEditor?.getJSON();

      setComparisonMode(false);
      setComparisonVisible(false);
      clearPendingComparison();

      const invocationId = comparisonInvocationIdRef.current;
      if (invocationId) {
        sendInvocationEvent(invocationId, "comparison-pick", pickedStream);
      }

      if (pickedStream === "B" && streamBSceneIndexRef.current != null) {
        setScenes((draft) => {
          draft[sceneId].lastCommandIndex = streamBSceneIndexRef.current;
          draft[sceneId].restartFromIndex = streamBSceneIndexRef.current;
        });
      }

      if (json?.content && editor) {
        requestAnimationFrame(() => {
          editor.commands.focus("end");
          editor.commands.insertContent(json.content);
          editorEndRef?.current?.scrollIntoView({ behavior: "instant", block: "end" });
        });
      }
    },
    [editor, clearPendingComparison, editorEndRef, setScenes, sceneId],
  );

  useEffect(() => {
    setComparisonMode(false);
    setComparisonVisible(false);
    if (!documentId || !existingCardsLoaded) return;
    const userId = getUserId();
    if (!userId) return;

    let cancelled = false;

    const toHtml = (text) => {
      if (!text) return null;
      return text.split("\n").map((line) => (line.trim() ? `<p>${line}</p>` : "<p></p>")).join("");
    };

    const populateEditors = (htmlA, htmlB) => {
      let attempts = 0;
      const tryPopulate = () => {
        if (cancelled) return;
        const refA = comparisonEditorsRef.current?.editorA;
        const refB = comparisonEditorsRef.current?.editorB;
        if (refA && refB) {
          if (htmlA) refA.commands.setContent(htmlA);
          if (htmlB) refB.commands.setContent(htmlB);
          setComparisonVisible(true);
        } else if (attempts < 20) {
          attempts++;
          setTimeout(tryPopulate, 50);
        }
      };
      tryPopulate();
    };

    db.collection("userData").doc(userId).collection("documentMetas").doc(documentId)
      .get()
      .then((doc) => {
        if (cancelled) return;
        const data = doc.data();
        const invocationId = data?.pendingComparisonInvocationId;
        if (!invocationId) return;

        const card = existingCards.find((c) => c.model.data.invocationId === invocationId);
        // Benchmark choices belong to the takeover. Older cards only carry the
        // model label; newer ones also have an explicit session marker.
        if (card?.model.data.benchmarkSessionId || card?.model.data.chiclets?.model === BENCHMARK_MODEL_LABEL) {
          clearPendingComparison();
          return;
        }
        if (!card || !Array.isArray(card.model.data.content) || card.model.data.content.length < 2) {
          clearPendingComparison();
          return;
        }

        const htmlA = toHtml(card.model.data.content[0]);
        const htmlB = toHtml(card.model.data.content[1]);
        if (!htmlA && !htmlB) {
          clearPendingComparison();
          return;
        }

        comparisonInvocationIdRef.current = invocationId;
        streamBSceneIndexRef.current = data.pendingComparisonStreamBSceneIndex ?? null;
        setComparisonMode(true);
        populateEditors(htmlA, htmlB);
      })
      .catch(() => {});

    return () => { cancelled = true; };
  }, [documentId, existingCardsLoaded, existingCards, clearPendingComparison]);

  return {
    comparisonMode,
    setComparisonMode,
    comparisonVisible,
    setComparisonVisible,
    comparisonEditorsRef,
    comparisonInvocationIdRef,
    streamBSceneIndexRef,
    handlePick,
  };
}
